// TestHistoryExportButton.js
import React, { useState } from 'react';
import { Button, CircularProgress, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TestHistoryExportDialog from './TestHistoryExportDialog';
import { handleDownloadTestHistoryExcel } from '../../utilities/export/handleDownloadTestHistoryExcel';
import { getTestResultDetail } from '../../services/testHistoryApiService';
import { useDispatch } from 'react-redux';
import { openJobsDialog } from '../../redux/confluence/confluenceJobsSlice';

export default function TestHistoryExportButton({
  allResults = [],
  filteredResults = [],
  hasActiveFilters = false,
  disabled = false,
  regressionTestId = '',
  searchFilters = {},
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, stage: '' });
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleExport = async (options) => {
    const { exportMode, includeOutbound, regressionTag } = options;

    setIsExporting(true);
    setError(null); // Clear any previous errors
    setExportProgress({ current: 0, total: 0, stage: 'Preparing...' });

    try {
      // Determine which results to export (summary data only at this point)
      const summaryResults = exportMode === 'all' ? allResults : filteredResults;
      const totalResults = summaryResults.length;

      setExportProgress({ current: 0, total: totalResults, stage: 'Fetching details' });

      // Fetch full details for each result
      const detailedResults = [];
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < summaryResults.length; i++) {
        const summary = summaryResults[i];

        try {
          setExportProgress({
            current: i + 1,
            total: totalResults,
            stage: 'Fetching details',
          });

          // Fetch full details including validation_state
          const detailedResult = await getTestResultDetail(summary.id);

          if (detailedResult && detailedResult.validation_state) {
            detailedResults.push(detailedResult);
            successCount++;
          } else {
            console.warn(`Result ${summary.id} fetched but has no validation_state`);
            failCount++;
          }
        } catch (error) {
          console.error(`Failed to fetch details for result ${summary.id}:`, error);
          failCount++;
          // Continue with other results
        }

        // Small delay to avoid overwhelming the API
        if (i < summaryResults.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Check if we got any valid results
      if (detailedResults.length === 0) {
        setError('Could not fetch detailed data for any test results. Please try again.');
        return;
      }

      // Show warning if some results failed
      if (failCount > 0) {
        console.warn(
          `Failed to fetch ${failCount} of ${totalResults} results. Proceeding with ${successCount} results.`,
        );
      }

      // Build filter description for filename
      let filterDescription = '';
      if (exportMode === 'filtered' && hasActiveFilters) {
        filterDescription = 'Filtered';
      }

      // Generate Excel file
      setExportProgress({
        current: totalResults,
        total: totalResults,
        stage: 'Generating Excel',
      });

      // Call the export utility with detailed results
      await handleDownloadTestHistoryExcel(detailedResults, {
        includeOutboundBeacons: includeOutbound,
        regressionTag,
        filterDescription,
      });

      // Small delay to allow download to initiate
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error exporting test history:', error);
      setError('An error occurred while exporting. Please try again.');
    } finally {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0, stage: '' });
    }
  };

  // Generate button text based on export state
  const getButtonText = () => {
    if (!isExporting) return 'Export';

    if (exportProgress.stage === 'Fetching details') {
      return `Fetching ${exportProgress.current} of ${exportProgress.total}`;
    } else if (exportProgress.stage === 'Generating Excel') {
      return 'Generating Excel...';
    } else {
      return 'Preparing...';
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
        onClick={handleOpenDialog}
        disabled={disabled || isExporting || filteredResults.length === 0}
        sx={{
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.light',
          },
          '&:disabled': {
            bgcolor: 'text.secondary',
          },
        }}
      >
        {getButtonText()}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TestHistoryExportDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onExport={handleExport}
        totalResults={allResults.length}
        filteredResults={filteredResults.length}
        hasActiveFilters={hasActiveFilters}
        initialRegressionTag={regressionTestId}
        searchFilters={searchFilters}
      />
      <Button
        variant="contained"
        onClick={() => dispatch(openJobsDialog())}
        sx={{
          backgroundColor: 'info.dark',
          color: 'background.paper',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'info.dark',
          },
        }}
      >
        VIEW EXPORTED CONFLUENCE
      </Button>
    </>
  );
}
